import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import AutoSuggest from "react-autosuggest";
import classNames from "classnames";
import { useSelector } from "react-redux";

import { isValidUrl } from "src/app/modals/AddCreatorsModal/components/GenerateCreatorFromString";
import { cancelRequestAxios } from "./utils/CancelRequest";
import { renderFakeSuggestion } from "../../modals/AddCreatorsModal/components/RenderSuggestion";

import "./AutoSuggestionInput.scss";

const cancelRequestAxiosObject = new cancelRequestAxios();

export function AutoSuggestionInput({
  placeholder,
  value,
  setValue,
  queryUrl,
  getSuggestionValue,
  renderSuggestion,
  renderSelectedOptions,
  storedObjects,
  setStoredObjects,
  threshold = 0,
  generateObject,
  generateObjectFromMultipleValues,
  globalTaskMode,
}) {
  const areaRef = useRef(null);
  const autoSuggestRef = useRef(null);
  const [isFocused, setFocus] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const {
    mainReducer: { activeWorkspaceUuid },
  } = useSelector((state) => state);

  useEffect(() => {
    autoSuggestRef.current.autowhatever.onFocus();
  }, [value]);

  const searchForResults = async (v, url) => {
    if (v.length >= threshold) {
      cancelRequestAxiosObject.cancelAndCreateToken();
      try {
        const response = await axios.get(url, {
          params: {
            query: v,
            wsWorkspaceUuid: activeWorkspaceUuid,
          },
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
          },
          withCredentials: true,
          cancelToken: cancelRequestAxiosObject.cancel_resquest.token,
        });
        cancelRequestAxiosObject.resetCancelToken();
        return response.data;
      } catch (error) {
        if (axios.isCancel(error)) {
          // console.error("Request canceled", error.message);
        }
      }
    }
  };

  const getSuggestions = (value) => {
    searchForResults(value, queryUrl).then((res) => {
      setSuggestions(res?.content ?? []);
    });
  };

  const setSelectedValue = (newValue) => {
    setStoredObjects((prev) => [...prev, newValue]);
    setValue("");
    areaRef.current.innerHTML = "";
  };

  const setNewObjectFromValue = () => {
    if (value.length > 0) {
      setSelectedValue(generateObject(value));
    }
  };

  const handleInput = (e) => {
    setValue(e.target.innerText.trimStart());
    setTimeout(() => {
      autoSuggestRef.current.autowhatever.onHighlightedItemChange();
    }, "200");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setNewObjectFromValue();
    }

    if (e.key === " " && !value.length) {
      e.preventDefault();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Backspace" && value.length === 0) {
      setStoredObjects(storedObjects.slice(0, storedObjects.length - 1));
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedData = clipboardData.getData("text/plain");
    const pastedHtml = clipboardData.getData("text/html");
    areaRef.current.textContent = pastedData;
    setCaret();

    const pastedObjects = getPastedObjects(pastedData, pastedHtml);

    pastedObjects.forEach((objects) => {
      const urls = objects.filter(isValidUrl);

      if (urls.length === objects.length) {
        setSelectedValue(
          urls.length > 1 && generateObjectFromMultipleValues
            ? generateObjectFromMultipleValues(urls)
            : generateObject(urls[0]),
        );
      } else {
        setValue(objects.join(" ").trim());
      }
    });
  };

  const getPastedObjects = (pastedData, pastedHtml) => {
    const spreadsheetObjects = getSpreadsheetObjectsFromHtml(pastedHtml);

    if (spreadsheetObjects.length) {
      return spreadsheetObjects;
    }

    const pastedRows = pastedData.trim().split(/\r?\n/);
    const isSpreadsheetData = pastedRows.some((row) => row.includes("\t"));
    const isSingleMultilineCell =
      pastedRows.length > 1 && pastedRows.every((row) => isValidUrl(row.trim()));

    if (isSingleMultilineCell) {
      return [pastedRows.map((row) => row.trim())];
    }

    if (isSpreadsheetData) {
      return pastedRows.map((row) => row.trim().split(/\t+/).filter(Boolean));
    }

    return pastedData.trim().split(/\s+/).map((obj) => [obj]);
  };

  const getSpreadsheetObjectsFromHtml = (pastedHtml) => {
    if (!pastedHtml) {
      return [];
    }

    const document = new DOMParser().parseFromString(pastedHtml, "text/html");
    const rows = Array.from(document.querySelectorAll("tr"));

    return rows
      .map((row) => {
        const cells = Array.from(row.querySelectorAll("td, th"));

        return cells.flatMap((cell) => getCellText(cell).trim().split(/\s+/));
      })
      .filter((row) => row.length);
  };

  const getCellText = (cell) => {
    const clone = cell.cloneNode(true);

    clone.querySelectorAll("br").forEach((br) => {
      br.replaceWith("\n");
    });

    return clone.textContent ?? "";
  };

  const setCaret = () => {
    const range = document.createRange();
    const sel = window.getSelection();

    sel.removeAllRanges();
    range.selectNodeContents(areaRef.current);
    range.collapse(false);
    sel.addRange(range);
    areaRef.current.focus();
  };

  const renderTextArea = (inputProps) => {
    return (
      <div
        className={classNames(
          "ws-textarea",
          "ws-textarea--auto-grow",
          value.length === 0 &&
            storedObjects.length === 0 &&
            "ws-textarea--empty",
        )}
        onClick={() => {
          if (value.length > 0) setCaret();
          areaRef.current.focus();
        }}
        onMouseDownCapture={(e) => {
          if (e.target !== areaRef.current) e.preventDefault();
        }}
      >
        <span className="ws-textarea-placeholder">{placeholder}</span>
        {renderSelectedOptions(storedObjects)}
        <span
          contentEditable="true"
          className="real-input"
          onInput={handleInput}
          {...inputProps}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    );
  };

  const renderSuggestionsContainer = ({ containerProps, children }) => {
    return (
      <div {...containerProps}>
        {children}
        {renderFakeSuggestion(
          containerProps.className.includes(
            "react-autosuggest__suggestions-container--open",
          ) ||
            (value.length > 0 && isFocused),
          value,
          setNewObjectFromValue,
        )}
      </div>
    );
  };

  useEffect(() => {
    if (typeof value !== "string") return;
    getSuggestions(value);
  }, [value]);

  return (
    <AutoSuggest
      suggestions={suggestions}
      onSuggestionsFetchRequested={() => {}}
      onSuggestionSelected={(event, { suggestion }) => {
        if (suggestion.hasCreatorDatabaseProfile && globalTaskMode) return;
        setSelectedValue(suggestion);
      }}
      renderInputComponent={renderTextArea}
      getSuggestionValue={getSuggestionValue}
      renderSuggestion={renderSuggestion}
      renderSuggestionsContainer={renderSuggestionsContainer}
      inputProps={{
        onFocus: () => setFocus(true),
        onBlur: () => setFocus(false),
        onChange: () => {},
        value,
        ref: areaRef,
      }}
      ref={autoSuggestRef}
    />
  );
}
